<script>
// [spec-011 T003] Textos deste componente NÃO vão para `constants/TextChat.js`
// (em edição paralela por outro dev nesta mesma rodada) nem para
// `constants/Text.js` (já no teto de ~500 linhas, hoje em 618) — constante
// própria do componente, mesmo padrão de outros arquivos pequenos e
// autocontidos do módulo.
const FEEDBACK_BUTTONS_TEXT = {
  placeholderComentario: 'Comentário (opcional): o que podia ser melhor?',
  botaoEnviar: 'Enviar',
  botaoCancelar: 'Cancelar',
  comentarioDadoPrefixo: 'Comentário: ',
}

// = VARCHAR do contrato do backend (spec-011 US-2, AC2.1/AC2.3;
// `resource_feedback.py::_LIMITE_COMENTARIO`, migration v634).
const COMENTARIO_MAX_LENGTH = 2000

export default {
  name: 'IntelligenceV2FeedbackButtons',
  props: {
    messageId: {
      type: String,
      required: true,
    },
    currentFeedback: {
      type: String,
      default: null,
    },
    // [spec-011 T003] Comentário já persistido para ESTA mensagem
    // (`_mensagens.feedback_comentario`) — mostrado ao reabrir a conversa,
    // sem reabrir o campo de edição.
    currentComentario: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      feedbackButtonsText: FEEDBACK_BUTTONS_TEXT,
      comentarioMaxLength: COMENTARIO_MAX_LENGTH,
      // Qual botão abriu o campo de comentário agora — 'thumbs_up' |
      // 'thumbs_down' | null. Não reaproveita `currentFeedback` (o que já
      // foi GRAVADO no servidor) porque o consultor pode reabrir o campo
      // pra trocar de thumb sem que o clique anterior já tenha voltado.
      feedbackEmEdicao: null,
      comentarioRascunho: '',
    }
  },
  computed: {
    feedbackState() {
      return {
        thumbsUp: {
          active: this.currentFeedback === 'thumbs_up',
          color: this.currentFeedback === 'thumbs_up' ? 'success' : 'secondary',
          icon: 'like',
        },
        thumbsDown: {
          active: this.currentFeedback === 'thumbs_down',
          color:
            this.currentFeedback === 'thumbs_down' ? 'danger' : 'secondary',
          icon: 'dislike',
        },
      }
    },
    // Mostra o comentário já dado só quando NÃO há edição em andamento — o
    // textarea aberto já é a própria "visão" do comentário (pré-preenchido
    // por `abrirComentario`).
    mostrarComentarioJaDado() {
      return Boolean(
        !this.feedbackEmEdicao && this.currentFeedback && this.currentComentario
      )
    },
  },
  methods: {
    // [spec-011 T003, AC2.1] Clicar no 👍/👎 abre o campo opcional de
    // comentário — o envio de verdade (com ou sem texto) só acontece em
    // `enviarComentario`, disparado pelo botão "Enviar" do popover.
    giveFeedback(feedback) {
      if (this.feedbackEmEdicao === feedback) {
        // Clicar de novo no mesmo botão fecha o campo sem reenviar.
        this.fecharComentario()
        return
      }
      this.feedbackEmEdicao = feedback
      this.comentarioRascunho = this.currentFeedback === feedback
        ? this.currentComentario || ''
        : ''
    },
    fecharComentario() {
      this.feedbackEmEdicao = null
      this.comentarioRascunho = ''
    },
    // "Enviar sem texto continua valendo" (brief da task) — comentário vazio
    // vira `null` no payload (nunca string vazia), pra bater com a regra do
    // backend de "ausência preserva o valor já gravado" (`resource_
    // feedback.py`: só entra no UPDATE quando o cliente mandou algo).
    enviarComentario() {
      const feedback = this.feedbackEmEdicao
      if (!feedback) return
      // Corta no limite em vez de recusar — o contador já orienta o
      // consultor; isto aqui é só a rede de segurança contra colar um texto
      // maior que o `maxlength` do textarea (paste, autofill etc).
      const texto = this.comentarioRascunho
        .slice(0, this.comentarioMaxLength)
        .trim()
      this.$emit('feedback', {
        messageId: this.messageId,
        feedback: feedback,
        comentario: texto || null,
      })
      this.fecharComentario()
    },
  },
}
</script>

<template>
  <div class="feedback-buttons">
    <div class="feedback-buttons__linha d-flex align-center">
      <v-btn
        icon
        x-small
        :color="feedbackState.thumbsUp.color"
        :aria-pressed="String(feedbackState.thumbsUp.active)"
        aria-label="Marcar resposta como útil"
        title="Marcar como útil"
        @click="giveFeedback('thumbs_up')"
      >
        <icon-svg
          :name="feedbackState.thumbsUp.icon"
          :size="15"
          :color="feedbackState.thumbsUp.color"
        />
      </v-btn>
      <v-btn
        icon
        x-small
        :color="feedbackState.thumbsDown.color"
        :aria-pressed="String(feedbackState.thumbsDown.active)"
        aria-label="Marcar resposta como não útil"
        title="Marcar como não útil"
        @click="giveFeedback('thumbs_down')"
      >
        <icon-svg
          :name="feedbackState.thumbsDown.icon"
          :size="15"
          :color="feedbackState.thumbsDown.color"
        />
      </v-btn>
    </div>

    <div
      v-if="feedbackEmEdicao"
      class="feedback-buttons__comentario mt-1"
      data-test="feedback-buttons-comentario"
    >
      <input-by-type
        v-model="comentarioRascunho"
        type="textarea"
        :counter="comentarioMaxLength"
        :maxlength="comentarioMaxLength"
        :placeholder="feedbackButtonsText.placeholderComentario"
        rows="2"
        outlined
        dense
        data-test="feedback-buttons-textarea"
      />
      <div class="feedback-buttons__comentario-rodape d-flex align-center justify-end">
        <div class="feedback-buttons__comentario-acoes d-flex">
          <BaseButton
            underlined
            fit-content
            data-test="feedback-buttons-cancelar"
            @click="fecharComentario"
          >
            {{ feedbackButtonsText.botaoCancelar }}
          </BaseButton>
          <BaseButton
            primary
            fit-content
            data-test="feedback-buttons-enviar"
            @click="enviarComentario"
          >
            {{ feedbackButtonsText.botaoEnviar }}
          </BaseButton>
        </div>
      </div>
    </div>

    <div
      v-else-if="mostrarComentarioJaDado"
      class="feedback-buttons__comentario-dado ia-texto-auxiliar"
      :title="currentComentario"
      data-test="feedback-buttons-comentario-dado"
    >
      {{ feedbackButtonsText.comentarioDadoPrefixo }}{{ currentComentario }}
    </div>
  </div>
</template>

<style lang="sass" scoped>
/* Antes só aparecia no hover do balão (opacity:0 + transition) — agora faz
   parte da barra de ações do rodapé (chat-message.vue), sempre visível, não
   mais um overlay sobre um balão que nem existe mais. */
.feedback-buttons
  display: flex
  flex-direction: column

.feedback-buttons__linha
  gap: 2px

/* v-btn "icon x-small" do Vuetify ainda reserva ~24px de touch target —
   bem maior que o ícone de 15px em si, e é isso (mais o margin-top) que
   deixava um vão grande abaixo do texto mesmo com os ícones discretos. */
.feedback-buttons ::v-deep .v-btn.v-btn--icon.v-size--x-small
  height: 20px
  width: 20px
  transition: background-color 0.15s ease

  // Mesmo efeito de hover dos ícones do header do chat
  // (top-chat-ia-pop-up.vue) — lá é branco translúcido pq o fundo é escuro;
  // aqui o fundo é claro, então a mesma ideia usa preto translúcido.
  &:hover
    background-color: rgba(0, 0, 0, 0.06) !important

// [spec-011 T003] Popover inline do comentário — nasce colado ao par de
// botões, largura contida (não estica pro tamanho do balão da mensagem).
.feedback-buttons__comentario
  max-width: 280px

.feedback-buttons__comentario-acoes
  gap: 4px

.feedback-buttons__comentario-dado
  color: var(--neutral-600)
  max-width: 280px
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap
</style>
